import {
  BuildingPackage,
  DocumentPackage,
  LayoutOptimization,
  BuildingPackageType,
  DocumentPackageType,
  LayoutType,
  OrganizationType,
  ServiceEntityType,
  ServiceEntityTypeLabels,
} from '@consigli/types';

import { Service } from './types';

/**
 * Type guard to check if a ServiceData object is a BuildingPackage
 */
export const isBuildingPackage = (service: Service): service is BuildingPackage => {
  return Object.values(BuildingPackageType).includes(service.type as BuildingPackageType);
};

/**
 * Type guard to check if a ServiceData object is a DocumentPackage
 */
export const isDocumentPackage = (service: Service): service is DocumentPackage => {
  return Object.values(DocumentPackageType).includes(service.type as DocumentPackageType);
};

/**
 * Type guard to check if a ServiceData object is a LayoutOptimization
 */
export const isLayoutOptimization = (service: Service): service is LayoutOptimization => {
  return Object.values(LayoutType).includes(service.type as LayoutType);
};

/**
 * Utility function to get the specific type of a ServiceData object
 */
export const getServiceType = (service: Service): ServiceEntityType => {
  if (isBuildingPackage(service)) {
    return ServiceEntityType.BUILDING_PACKAGE;
  }
  if (isLayoutOptimization(service)) {
    return ServiceEntityType.LAYOUT_OPTIMIZATION;
  }
  if (isDocumentPackage(service)) {
    return ServiceEntityType.DOCUMENT_PACKAGE;
  }
  // Fallback: treat any unrecognised type as a document package to avoid crashing on
  // newly added DocumentPackageType values that haven't rolled out to this client yet.
  console.warn(
    `Unrecognised service type "${(service as { type: string }).type}" — treating as document package.`,
  );
  return ServiceEntityType.DOCUMENT_PACKAGE;
};

/**
 * Utility function to get a human-readable label for the service entity type
 */
export const getServiceTypeLabel = (service: Service): string => {
  return ServiceEntityTypeLabels[getServiceType(service)];
};

export type ServiceDeleteParams =
  | { type: ServiceEntityType.BUILDING_PACKAGE; params: { projectId: number; packageId: string } }
  | { type: ServiceEntityType.DOCUMENT_PACKAGE; params: { projectId: number; packageId: string } }
  | {
      type: ServiceEntityType.LAYOUT_OPTIMIZATION;
      params: { projectId: number; layoutOptimizationId: string };
    };

/**
 * Utility function to get the appropriate delete function parameters based on service type
 */
export const getDeleteParams = (service: Service, projectId: number): ServiceDeleteParams => {
  if (isBuildingPackage(service)) {
    return {
      type: ServiceEntityType.BUILDING_PACKAGE,
      params: {
        projectId,
        packageId: service.id,
      },
    };
  }

  if (isDocumentPackage(service)) {
    return {
      type: ServiceEntityType.DOCUMENT_PACKAGE,
      params: {
        projectId,
        packageId: service.id,
      },
    };
  }

  if (isLayoutOptimization(service)) {
    return {
      type: ServiceEntityType.LAYOUT_OPTIMIZATION,
      params: {
        projectId,
        layoutOptimizationId: service.id,
      },
    };
  }

  // Fallback for unrecognised but document-like service types.
  console.warn(
    `Unrecognised service type "${(service as { type: string }).type}" — treating as document package for delete.`,
  );
  return {
    type: ServiceEntityType.DOCUMENT_PACKAGE,
    params: {
      projectId,
      packageId: (service as { id: string }).id,
    },
  };
};

export const isString = (value: unknown): value is string => typeof value === 'string';
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
export const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
export const isOrganizationType = (value: unknown): value is OrganizationType =>
  value !== null &&
  value !== undefined &&
  Object.values(OrganizationType).includes(value as OrganizationType);
